import axios from "axios";
import { UnitSystem } from "types/units";

import { absolutizeServiceRoot } from "components/map/utils/absolutizeServiceRoot";

/** The user's display unit, per request: "0" Hybrid, "1" SI, "2" Imperial. */
export const DISPLAY_UNIT_HEADER = "X-Display-Unit";

let displayUnit: Maybe<string>;

/**
 * Roots of the services that read the display unit or pass it on to one that does: the
 * readers in the header contract (https://novilabs.atlassian.net/browse/EVA-275?focusedCommentId=165848).
 * `/filter` is served by data-svc-rs. The header goes only to these: several other services
 * list their allowed CORS headers explicitly, and a header they don't allow blocks the whole
 * request wherever the UI is cross-origin (local dev, dev-test). A service that starts reading
 * the unit must be added here. Written out one by one because Vite substitutes each
 * `process.env.REACT_APP_*` expression at build time.
 */
function readerRoots(): Maybe<string>[] {
  return [
    process.env.REACT_APP_CHART_SERVICE,
    process.env.REACT_APP_DATA_RS_ROOT,
    process.env.REACT_APP_FILTER_SERVICE,
    process.env.REACT_APP_MAP_SERVICE,
    process.env.REACT_APP_MAP_RS_SERVICE,
    process.env.REACT_APP_PAD_SCENARIO_SERVICE,
    process.env.REACT_APP_ML_SERVICE
  ];
}

// On a path boundary, so `/data` doesn't claim `/data-rs` or `/database`.
function isUnder(url: string, root: string): boolean {
  const base = root.replace(/\/+$/, "");
  return (
    !!base && (url === base || url.startsWith(`${base}/`) || url.startsWith(`${base}?`))
  );
}

/**
 * The display unit to send with a request to `url`: nothing when that service doesn't use it,
 * or when no unit is known yet. The url and the roots are both compared in absolute form, so a
 * relative root matches the absolute tile urls Mapbox gets, and a relative request url (always
 * same-origin) matches an absolute root on the page's own host.
 */
export function displayUnitFor(url: string): Maybe<string> {
  if (!displayUnit) {
    return undefined;
  }
  const target = absolutizeServiceRoot(url);
  const reads = readerRoots().some(
    (root) => !!root && isUnder(target, absolutizeServiceRoot(root))
  );
  return reads ? displayUnit : undefined;
}

/**
 * Sets the unit later requests carry, or clears it when the unit is unknown or not one of
 * 0, 1 and 2. With no header the services fall back to the token's claim; a guessed or
 * leftover value would pin a unit the user did not choose.
 */
export function setDisplayUnitHeader(unit: Maybe<UnitSystem | number | string>): void {
  const value = typeof unit === "number" || typeof unit === "string" ? String(unit) : "";
  displayUnit = /^[012]$/.test(value) ? value : undefined;
}

// The global instance only: the separate instances (customer-management) talk to hosts that
// don't use the unit.
axios.interceptors.request.use((config) => {
  const unit = displayUnitFor(axios.getUri(config));
  if (unit) {
    config.headers.set(DISPLAY_UNIT_HEADER, unit);
  }
  return config;
});
